import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Clock, Gauge, TrendingUp, Zap, AlertTriangle, Info } from "lucide-react";
import { getQuota, getBurndown } from "../services/api";
import { QUOTA_STATUS } from "../types/quota";
import type { BurndownPoint, QuotaSnapshot, QuotaState } from "../types/quota";

/**
 * THE WEEK — the pacing governor, drawn.
 *
 * The subscription's weekly quota does not roll over. Whatever is unspent when
 * it resets is gone. So this panel is not a fuel gauge with a red line at the
 * top; it is a corridor, and being UNDER it is a problem worth a warning colour
 * in exactly the same way as being over it.
 *
 * Everything here is measured. While the budget is still being calibrated there
 * is no corridor to draw, and the panel says so rather than drawing a plausible
 * one — a governor that shows a confident band around an invented number is
 * worse than one that admits it does not know yet.
 */

const POLL_MS = 30_000;

const pct = (v: number | null | undefined): string =>
  v === null || v === undefined ? "—" : `${Math.round(v * 100)}%`;

function dur(secs: number): string {
  if (secs <= 0) return "now";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function WeekPanel() {
  const [snap, setSnap] = useState<QuotaSnapshot | null>(null);
  const [series, setSeries] = useState<BurndownPoint[]>([]);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const issued = useRef(0);

  const load = useCallback(async () => {
    const at = ++issued.current;
    try {
      const [s, b] = await Promise.all([getQuota(), getBurndown()]);
      // A slower earlier request must not overwrite a newer one.
      if (at < issued.current) return;
      setSnap(s);
      setSeries(b.items);
      setErr(false);
    } catch {
      if (at < issued.current) return;
      setErr(true);
    } finally {
      if (at === issued.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => {
      // Nothing here is worth polling for while the tab is in the background.
      if (!document.hidden) void load();
    }, POLL_MS);
    const wake = () => { if (!document.hidden) void load(); };
    document.addEventListener("visibilitychange", wake);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", wake); };
  }, [load]);

  if (err) {
    return (
      <section className="card jarvis-panel jarvis-week">
        <header className="jarvis-panel-head"><h3>The week</h3></header>
        <p className="muted" style={{ margin: 0 }}>
          The governor is unreachable, so any number here would be made up.
        </p>
      </section>
    );
  }

  const s = snap?.state;
  const d = snap?.decision;
  const meta = s ? QUOTA_STATUS[s.status] : null;

  return (
    <section className="card jarvis-panel jarvis-week">
      <header className="jarvis-panel-head">
        <h3>The week</h3>
        {meta ? (
          <span className={"jarvis-chip" + (meta.tone ? " tone-" + meta.tone : "")}>{meta.label}</span>
        ) : null}
        <span className="faint" style={{ marginLeft: "auto" }}>
          {s ? <><Clock size={12} /> resets in {dur(s.secsToReset)}</> : loading ? "reading…" : null}
        </span>
      </header>

      {!s ? (
        <p className="muted" style={{ margin: 0 }}>{loading ? "Reading the governor…" : "No governor state."}</p>
      ) : (
        <>
          <Burndown series={series} state={s} />

          <div className="jarvis-week-figures">
            <Figure
              icon={<Gauge size={13} />}
              label="spent this week"
              value={s.calibrating ? s.spent.toFixed(1) : pct(s.spentPct)}
              sub={s.calibrating ? "no measured ceiling yet" : `of ${s.weeklyBudget.toFixed(0)}`}
            />
            <Figure
              icon={<TrendingUp size={13} />}
              label="projected finish"
              value={s.calibrating ? "—" : pct(s.projectedEnd)}
              sub={s.calibrating ? "needs two full weeks" : `target ${pct(s.target / (s.weeklyBudget || 1))}`}
              tone={!s.calibrating && s.projectedEnd !== null && s.projectedEnd < 0.75 ? "warn" : ""}
            />
            <Figure
              icon={<Clock size={13} />}
              label="to the 90% deadline"
              value={dur(s.secsToDeadline)}
              sub={`then ${dur(s.secsToReset - s.secsToDeadline)} of reserve`}
            />
            <Figure
              icon={<Zap size={13} />}
              label="running now"
              value={String(s.inFlight)}
              sub={d ? `governor wants ${d.workers}` : ""}
              tone={d && d.delta !== 0 ? "warn" : ""}
            />
          </div>

          <SessionBar state={s} />

          {d ? (
            <p className={"jarvis-week-decision" + (d.sprintRefused || d.hold ? " tone-warn" : "")}>
              {d.sprintRefused || d.hold ? <AlertTriangle size={12} /> : <Info size={12} />}
              <span>{d.reason}</span>
            </p>
          ) : null}

          {s.calibrating ? (
            <p className="faint jarvis-week-note">
              Calibrating. The fleet works normally and the governor only watches — it will not
              pace against a budget nobody has measured yet. Two full weeks of running gives it a
              real number; until then the only signal it acts on is an actual rate limit.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

function Figure({ icon, label, value, sub, tone }: {
  icon: ReactNode; label: string; value: string; sub?: string; tone?: string;
}) {
  return (
    <div className={"jarvis-week-figure" + (tone ? " tone-" + tone : "")}>
      <span className="jarvis-week-figure-label">{icon} {label}</span>
      <span className="jarvis-week-figure-value tnum">{value}</span>
      {sub ? <span className="faint">{sub}</span> : null}
    </div>
  );
}

/* ------------------------------------------------------------ the chart --- */

const W = 720;
const H = 210;
const PAD = { l: 44, r: 14, t: 12, b: 22 };

function Burndown({ series, state }: { series: BurndownPoint[]; state: QuotaState }) {
  const [hover, setHover] = useState<number | null>(null);

  const geom = useMemo(() => {
    const t0 = new Date(state.weekStart).getTime();
    const t1 = new Date(state.weekEnd).getTime();
    const span = Math.max(t1 - t0, 1);

    // While calibrating there is no budget, so scale to what has actually been
    // spent. An empty week scales to 1 so the axis is not degenerate.
    const top = state.calibrating
      ? Math.max(state.spent * 1.35, 1)
      : Math.max(state.weeklyBudget, state.spent, 1);

    const x = (iso: string) => PAD.l + ((new Date(iso).getTime() - t0) / span) * (W - PAD.l - PAD.r);
    const y = (v: number) => H - PAD.b - (v / top) * (H - PAD.t - PAD.b);
    return { t0, t1, span, top, x, y };
  }, [series, state]);

  const { x, y, top, t0, t1 } = geom;

  const path = (get: (p: BurndownPoint) => number): string =>
    series.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.at).toFixed(1)},${y(get(p)).toFixed(1)}`).join(" ");

  const corridor = useMemo(() => {
    if (state.calibrating || series.length < 2) return "";
    const up = series.map((p) => `${x(p.at).toFixed(1)},${y(p.ceiling).toFixed(1)}`);
    const down = [...series].reverse().map((p) => `${x(p.at).toFixed(1)},${y(p.floor).toFixed(1)}`);
    return `M${up.join(" L")} L${down.join(" L")} Z`;
  }, [series, state.calibrating, x, y]);

  const area = useMemo(() => {
    if (series.length < 2) return "";
    const line = series.map((p) => `${x(p.at).toFixed(1)},${y(p.spent).toFixed(1)}`);
    const last = series[series.length - 1];
    return `M${x(series[0].at).toFixed(1)},${y(0).toFixed(1)} L${line.join(" L")} L${x(last.at).toFixed(1)},${y(0).toFixed(1)} Z`;
  }, [series, x, y]);

  // Day gridlines, labelled in Jared's own timezone rather than the browser's,
  // because the reset is expressed in his.
  const days = useMemo(() => {
    const out: { at: number; label: string }[] = [];
    for (let d = 1; d < 7; d++) {
      const at = t0 + d * 86400_000;
      if (at >= t1) break;
      out.push({
        at,
        label: new Date(at).toLocaleDateString("en-ZA", { timeZone: state.timezone, weekday: "short" }),
      });
    }
    return out;
  }, [t0, t1, state.timezone]);

  const dx = PAD.l + ((new Date(state.deadline).getTime() - t0) / Math.max(t1 - t0, 1)) * (W - PAD.l - PAD.r);
  const nowP = series.length ? series[series.length - 1] : null;
  const hp = hover !== null ? series[hover] : null;

  return (
    <div className="jarvis-week-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img"
           aria-label={`Spend so far this week: ${state.spent.toFixed(1)}, status ${state.status}`}
           onMouseLeave={() => setHover(null)}
           onMouseMove={(e) => {
             if (!series.length) return;
             const r = e.currentTarget.getBoundingClientRect();
             const px = ((e.clientX - r.left) / r.width) * W;
             const frac = (px - PAD.l) / (W - PAD.l - PAD.r);
             const at = t0 + frac * (t1 - t0);
             let best = 0, bd = Infinity;
             series.forEach((p, i) => {
               const dd = Math.abs(new Date(p.at).getTime() - at);
               if (dd < bd) { bd = dd; best = i; }
             });
             setHover(best);
           }}>
        <defs>
          {/* Stop colours come from the stylesheet, not from literals here: the
              portal has a real light theme and a hard-coded blue is right in
              exactly one of them. */}
          <linearGradient id="wk-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" className="jarvis-week-stop-a" />
            <stop offset="100%" className="jarvis-week-stop-b" />
          </linearGradient>
          <filter id="wk-glow" x="-20%" y="-40%" width="140%" height="180%">
            <feGaussianBlur stdDeviation="2.6" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* the reserve window: deliberately not part of the plan */}
        <rect x={dx} y={PAD.t} width={Math.max(W - PAD.r - dx, 0)} height={H - PAD.t - PAD.b}
              className="jarvis-week-reserve" />

        {days.map((d) => {
          const px = PAD.l + ((d.at - t0) / Math.max(t1 - t0, 1)) * (W - PAD.l - PAD.r);
          return (
            <g key={d.at}>
              <line x1={px} y1={PAD.t} x2={px} y2={H - PAD.b} className="jarvis-week-grid" />
              <text x={px + 3} y={H - PAD.b + 14} className="jarvis-week-axis">{d.label}</text>
            </g>
          );
        })}

        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={PAD.l} y1={y(top * f)} x2={W - PAD.r} y2={y(top * f)} className="jarvis-week-grid" />
            <text x={PAD.l - 6} y={y(top * f) + 3} textAnchor="end" className="jarvis-week-axis">
              {state.calibrating ? (top * f).toFixed(0) : `${Math.round(f * 100)}%`}
            </text>
          </g>
        ))}

        {corridor ? <path d={corridor} className="jarvis-week-corridor" /> : null}
        {!state.calibrating && series.length > 1
          ? <path d={path((p) => p.ideal)} className="jarvis-week-ideal" />
          : null}

        {area ? <path d={area} fill="url(#wk-area)" /> : null}
        {series.length > 1
          ? <path d={path((p) => p.spent)} className="jarvis-week-actual" filter="url(#wk-glow)" />
          : null}

        <line x1={dx} y1={PAD.t} x2={dx} y2={H - PAD.b} className="jarvis-week-deadline" />
        <text x={dx - 5} y={PAD.t + 10} textAnchor="end" className="jarvis-week-axis">
          {pct(state.target / (state.weeklyBudget || 1))} due
        </text>

        {nowP ? (
          <circle cx={x(nowP.at)} cy={y(nowP.spent)} r={3.4} className="jarvis-week-now" />
        ) : null}

        {hp ? (
          <>
            <line x1={x(hp.at)} y1={PAD.t} x2={x(hp.at)} y2={H - PAD.b} className="jarvis-week-cursor" />
            <circle cx={x(hp.at)} cy={y(hp.spent)} r={3} className="jarvis-week-now" />
          </>
        ) : null}
      </svg>

      <p className="faint jarvis-week-readout tnum">
        {hp ? (
          <>
            {new Date(hp.at).toLocaleString("en-ZA", {
              timeZone: state.timezone, weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false,
            })}
            {" · spent "}{hp.spent.toFixed(1)}
            {state.calibrating ? null : <>{" · should be "}{hp.ideal.toFixed(1)}</>}
          </>
        ) : state.calibrating
          ? "Actual spend only. No corridor until the budget is measured."
          : "Band is the corridor, dashed is the pace line, the marker is when 90% is due."}
      </p>
    </div>
  );
}

/* ------------------------------------------------------ the 5-hour window - */

/**
 * The window that actually stops a worker mid-sentence. Being perfectly on
 * weekly pace and locked out for three hours is the same burst-then-idle
 * failure the whole fleet exists to kill, one time-scale down.
 */
function SessionBar({ state }: { state: QuotaState }) {
  if (state.calibrating || state.sessionCap <= 0) return null;
  const f = Math.min(state.sessionSpent / state.sessionCap, 1);
  const tone = f >= 1 ? "bad" : f > 0.8 ? "warn" : "";
  return (
    <div className={"jarvis-week-session" + (tone ? " tone-" + tone : "")}>
      <span className="jarvis-week-figure-label">5-hour window</span>
      <div className="jarvis-week-bar" role="meter" aria-valuenow={Math.round(f * 100)}
           aria-valuemin={0} aria-valuemax={100}>
        <i style={{ width: `${f * 100}%` }} />
      </div>
      <span className="tnum">{Math.round(f * 100)}%</span>
      {state.sessionResetsAt && f > 0.5 ? (
        <span className="faint">
          eases at {new Date(state.sessionResetsAt).toLocaleTimeString("en-ZA", {
            timeZone: state.timezone, hour: "2-digit", minute: "2-digit", hour12: false })}
        </span>
      ) : null}
    </div>
  );
}
